import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import styles from '../CreateTournament/CreateTournament.module.scss';
import { useGetTournament } from '../../hooks/useGetTournament';
import { Loader } from '../Shared/Loader/Loader';
import { GreenButton } from '../Shared/GreenButton/GreenButton';
import { TransparentButton } from '../Shared/TransparentButton/TransparentButton';
import { Input } from '../Shared/Input/Input';
import { Checkbox } from '../Shared/Checkbox/Checkbox';
import { ENDPOINTS, QueryKeys } from '../../constants';

const toDatetimeLocal = (isoString: string): string => {
    const d = new Date(isoString);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
};

export const EditTournament = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data: tournament, isLoading } = useGetTournament(Number(id));

    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [prizes, setPrizes] = useState<string[]>(['']);
    const [bet, setBet] = useState<number>(0);
    const [forceIncompleteStart, setForceIncompleteStart] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (tournament && !initialized) {
            setName(tournament.name);
            setStartDate(toDatetimeLocal(tournament.startDate));
            setPrizes(tournament.prizes.length > 0 ? tournament.prizes.map(String) : ['']);
            setBet(tournament.bet);
            setForceIncompleteStart((tournament as any).forceIncompleteStart ?? false);
            setInitialized(true);
        }
    }, [tournament, initialized]);

    const handlePrizeChange = (index: number, value: string) => {
        setPrizes(prev => prev.map((p, i) => (i === index ? value : p)));
    };

    const addPrize = () => setPrizes(prev => [...prev, '']);

    const removePrize = (index: number) => {
        setPrizes(prev => prev.filter((_, i) => i !== index));
    };

    const isValid =
        name.trim() !== '' &&
        startDate !== '' &&
        prizes.length > 0 &&
        prizes.every(p => p !== '' && !isNaN(Number(p))) &&
        bet >= 0;

    const handleSubmit = async () => {
        if (!isValid || !id) return;
        setSubmitting(true);
        setError(null);
        try {
            await axios.put(`${axios.defaults.baseURL}${ENDPOINTS.adminTournaments}/${id}`, {
                name: name.trim(),
                startDate: new Date(startDate).toISOString(),
                prizes: prizes.map(p => parseInt(p, 10)),
                bet: Number(bet),
                forceIncompleteStart,
            });
            queryClient.invalidateQueries(QueryKeys.tournamentsList);
            queryClient.invalidateQueries([QueryKeys.tournament, Number(id)]);
            navigate(`/tournaments/${id}`);
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to update tournament');
        } finally {
            setSubmitting(false);
        }
    };

    if (isLoading) return <Loader />;
    if (!tournament) return <div>Tournament not found</div>;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backButton} onClick={() => navigate(`/tournaments/${id}`)}>
                    ← Back
                </div>
                <div className={styles.title}>Edit Tournament — {tournament.name}</div>
            </div>

            <div className={styles.card}>
                <div className={styles.grid}>
                    <div className={styles.field}>
                        <label className={styles.label}>Name</label>
                        <Input
                            placeholder="Tournament name"
                            value={name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Game</label>
                        <input className={styles.select} value={tournament.game} disabled />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Game Version</label>
                        <input className={styles.select} value={tournament.gameVersion} disabled />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Mode</label>
                        <input className={styles.select} value={tournament.mode || '—'} disabled />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Start Date</label>
                        <input
                            type="datetime-local"
                            className={styles.select}
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Entry Fee</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            className={styles.select}
                            value={bet}
                            onChange={e => setBet(parseFloat(e.target.value) || 0)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Force Start</label>
                        <div className={styles.checkboxWrapper}>
                            <Checkbox
                                checked={forceIncompleteStart}
                                onChange={() => setForceIncompleteStart(!forceIncompleteStart)}
                            />
                            <span className={styles.checkboxLabel}>Start tournament even if not full</span>
                        </div>
                    </div>
                </div>

                <div className={styles.prizesSection}>
                    <div className={styles.prizeHeader}>
                        <div className={styles.sectionTitle}>Prizes</div>
                        {prizes.length < 3 && (
                            <div className={styles.addPrize} onClick={addPrize}>
                                + Add Prize
                            </div>
                        )}
                    </div>
                    {prizes.map((prize, index) => (
                        <div key={index} className={styles.prizeRow}>
                            <span className={styles.prizePlace}>#{index + 1}</span>
                            <input
                                type="number"
                                min={0}
                                className={styles.select}
                                placeholder={`Prize for place ${index + 1}`}
                                value={prize}
                                onChange={e => handlePrizeChange(index, e.target.value)}
                            />
                            {prizes.length > 1 && (
                                <div className={styles.removeBtn} onClick={() => removePrize(index)}>
                                    ✕
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {error && <div className={styles.error}>{error}</div>}

                <div className={styles.actions}>
                    <TransparentButton outline onClick={() => navigate(`/tournaments/${id}`)} noMargin>
                        Cancel
                    </TransparentButton>
                    <GreenButton onClick={handleSubmit} disabled={!isValid || submitting} noMargin>
                        {submitting ? 'Saving...' : 'Save'}
                    </GreenButton>
                </div>
            </div>
        </div>
    );
};
